import IMG_BG_DESKTOP from '~/assets/images/kv/ovenbreak/bg_desktop.jpg';
import IMG_BG_MOBILE from '~/assets/images/kv/ovenbreak/bg_mobile.jpg';
import VIDEO_KV_PC_H264 from '~/assets/images/kv/ovenbreak/kv_pc_h264.mp4';
import VIDEO_KV_PC_HEVC from '~/assets/images/kv/ovenbreak/kv_pc_hevc.mp4';
import IMG_LOGO_EN from '~/assets/images/kv/ovenbreak/logo_en.png';
import IMG_LOGO_JA from '~/assets/images/kv/ovenbreak/logo_ja.png';
import IMG_LOGO_KO from '~/assets/images/kv/ovenbreak/logo_ko.png';
import IMG_LOGO_TH from '~/assets/images/kv/ovenbreak/logo_th.png';
import IMG_LOGO_ZH_HANT from '~/assets/images/kv/ovenbreak/logo_zh-Hant.png';

import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';
import styled from '@emotion/styled';
import { rem } from 'polished';
import React from 'react';

import DownloadButtons from '~/widgets/download-buttons';
import { css, scaledCss } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';

const PlaceholderBackgroundPC = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${IMG_BG_DESKTOP})`,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  zIndex: -2,
});

const PlaceholderBackgroundMobile = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${IMG_BG_MOBILE})`,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  zIndex: -2,
});

const PcVideoBackground = styled.div({
  position: 'relative',
  overflow: 'hidden',
  display: 'none',
  '@media (min-width: 640px)': {
    display: 'block',
  },
  minHeight: '700px',
  height: '40.2083vw',
  maxHeight: '2000px',
});

const MobileVideoBackground = styled.div({
  position: 'relative',
  overflow: 'hidden',
  display: 'block',
  '@media (min-width: 640px)': {
    display: 'none',
  },
  minHeight: '500px',
  height: '150vw',
});

const VideoElement = styled.video({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'cover',
  zIndex: -1,
});

const LogoImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const getLogoByLocale = (locale: string) => {
  switch (locale.toLowerCase()) {
    case 'ko':
      return IMG_LOGO_KO;
    case 'ja':
      return IMG_LOGO_JA;
    case 'th':
      return IMG_LOGO_TH;
    case 'zh-hant':
      return IMG_LOGO_ZH_HANT;
    case 'zh-hans':
      return IMG_LOGO_ZH_HANT;
    case 'en':
    default:
      return IMG_LOGO_EN;
  }
};

const OvenbreakKeyVisual: React.FC = () => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const frontmatter = useFrontmatter();
  const locale = useLocale();

  const { logo } = frontmatter.top;
  const logoSrc = getLogoByLocale(locale);

  React.useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div css={css({ position: 'relative' })}>
      <PcVideoBackground>
        <PlaceholderBackgroundPC />
        <div
          css={css({
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
          })}
        >
          <div
            css={css({
              width: [400, 400, 400, 500],
              height: [184, 184, 184, 230],
              ...(logo.pc.margin && { marginBottom: logo.pc.margin }),
            })}
          >
            <LogoImage src={logoSrc} alt='logo' loading='eager' />
          </div>
          <DownloadButtons
            type='large'
            css={css({
              width: ['100vw', 700, 700, '100vw'],
              '> a': {
                width: 230,
                boxShadow: 'none',
                borderRadius: 0,
                marginTop: 10,
                ':not(:first-of-type)': {
                  marginLeft: 10,
                },
              },
              '> a > img': {
                boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                borderRadius: 80,
                width: '100%',
                height: 'auto',
              },
            })}
          />
        </div>
      </PcVideoBackground>
      <MobileVideoBackground>
        <PlaceholderBackgroundMobile />
        <div
          css={scaledCss({
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'absolute',
            top: logo.mobile.y,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
          })}
        >
          <div
            css={scaledCss({
              width: logo.mobile.w,
              height: logo.mobile.h,
              marginBottom: rem(-45),
              marginTop: rem(45),
            })}
          >
            <LogoImage src={logoSrc} alt='logo' loading='eager' />
          </div>
          <DownloadButtons
            type='small'
            css={css({
              paddingY: rem(5),
              width: '100%',
              maxWidth: 340,
              '> a': {
                width: `calc(50% - ${rem(4)})`,
                height: 'auto',
                marginTop: rem(5),
                '@media (min-width: 359px)': {
                  width: 156,
                  height: 50,
                  marginTop: rem(10),
                },
                ':not(:nth-of-type(odd))': {
                  marginLeft: [8, 0],
                },
              },
            })}
          />
        </div>
      </MobileVideoBackground>
      {isLoaded && (
        <VideoElement autoPlay loop muted playsInline preload='auto'>
          <source src={VIDEO_KV_PC_HEVC} type={'video/mp4; codec=hev1.1.6.H123.B0'} />
          <source src={VIDEO_KV_PC_H264} type={'video/mp4; codecs=avc1.42E01E'} />
        </VideoElement>
      )}
    </div>
  );
};

export default OvenbreakKeyVisual;
